import React from 'react';
import { Typography, Box } from '@mui/material';

/**
 * Parses text with **bold** markdown syntax and returns JSX elements
 * @param text - The text to parse
 * @param variant - Typography variant to use
 * @param sx - Additional styling
 * @returns JSX element with bold text rendered
 */
export const parseBoldText = (
  text: string, 
  variant: any = 'body1', 
  sx: any = {}
): React.ReactElement => {
  if (!text) return <></>;

  // Split text by **bold** patterns
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  const elements = parts.map((part, index) => {
    // Check if this part is wrapped in **
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove the ** markers and render as bold
      const boldText = part.slice(2, -2);
      return (
        <Typography
          key={index}
          component="span"
          variant={variant}
          sx={{
            fontWeight: 'bold',
            ...sx
          }}
        >
          {boldText}
        </Typography>
      );
    } else if (part.trim()) {
      // Regular text
      return (
        <Typography
          key={index}
          component="span"
          variant={variant}
          sx={sx}
        >
          {part}
        </Typography>
      );
    }
    return null;
  }).filter(Boolean);

  return (
    <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
      {elements}
    </Box>
  );
};

/**
 * Alternative function that returns a single Typography component with mixed content
 * @param text - The text to parse
 * @param variant - Typography variant to use
 * @param sx - Additional styling
 * @returns Single Typography component with mixed bold/regular text
 */
export const parseBoldTextInline = (
  text: string, 
  variant: any = 'body1', 
  sx: any = {}
): React.ReactElement => {
  if (!text) return <Typography variant={variant} sx={sx}></Typography>;

  // Split text by **bold** patterns
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  const content = parts.map((part, index) => {
    // Check if this part is wrapped in **
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove the ** markers and render as bold
      const boldText = part.slice(2, -2);
      return (
        <span key={index} style={{ fontWeight: 'bold' }}>
          {boldText}
        </span>
      );
    }
    return part;
  });

  return (
    <Typography 
      variant={variant} 
      sx={{ 
        whiteSpace: 'pre-wrap',
        ...sx 
      }}
    >
      {content}
    </Typography>
  );
};
